import { Typography } from 'ui/Typography';
import { Box } from 'ui/Box';
import { AuthContainer } from 'components/AuthContainer';
import { Input } from 'components/Input';
import { Button } from 'components/Button';
import { ArrowRightIcon } from 'components/icons/ArrowRightIcon';
import { useLogin } from 'modules/LoginModule/hooks/useLogin';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { appRoutes } from 'utils';
import { useFetchProfile } from 'hooks/useFetchProfile';
import { useUserStore } from 'store';
import { UserRole } from 'types/api.d';
import { passwordValidationRules } from 'utils/';

export const Login = () => {
  const passwordRef = useRef<HTMLInputElement>(null);
  const { status, error, handleLogin, setError } = useLogin();
  const { setUser } = useUserStore();
  const { status: fetchStatus, data, fetchProfile } = useFetchProfile();
  const navigate = useNavigate();

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = event.target as typeof event.target & {
      email: { value: string };
      password: { value: string };
    };

    if (!target.email.value && !target.password.value) {
      setError('Please fill in all the fields');
      return;
    }
    if (!passwordValidationRules.test(target.password.value)) {
      setError('Invalid password');
      return;
    }
    if (target.email.value && target.password.value) {
      setError(null);
      return handleLogin(target.email.value, target.password.value);
    }
  };

  const onForgotPasswordClick = () => {
    navigate(appRoutes.forgotPassword);
  };

  useEffect(() => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      navigate(appRoutes.home);
    }
  }, [navigate]);

  useEffect(() => {
    if (status === 'success' && fetchStatus === 'idle') {
      fetchProfile();
    }
  }, [status, navigate, fetchProfile, fetchStatus, data]);

  useEffect(() => {
    if (fetchStatus === 'success' && data) {
      setUser(data);
      if (data.company === null && data.role === UserRole.SA) {
        navigate(appRoutes.register);
      } else {
        navigate(appRoutes.home);
      }
    }
  }, [fetchStatus, data, navigate, setUser]);

  return (
    <AuthContainer title="Login">
      <Box
        component="form"
        onSubmit={onFormSubmit}
        className="flex w-full flex-col gap-2 md:max-w-2xl"
      >
        <Input
          type="email"
          placeholder="Email"
          name="email"
          error={!!error}
          tabIndex={1}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              passwordRef.current?.focus();
            }
          }}
        />
        <Input
          inputRef={passwordRef}
          tabIndex={2}
          type="password"
          placeholder="Password"
          name="password"
          error={!!error}
          helperText={error}
        />
        <Box className="mt-9 flex justify-center gap-4">
          <Button
            onClick={onForgotPasswordClick}
            type="button"
            variantType="outlined"
            className="w-full max-w-48 px-0"
          >
            <Typography variant="buttonText" className="font-normal">
              Forgot Password?
            </Typography>
          </Button>
          <Button
            type="submit"
            className="w-full max-w-48"
            disabled={status === 'loading'}
          >
            <Typography variant="buttonText" className="font-semibold">
              Login
            </Typography>
            <ArrowRightIcon className="ml-2" />
          </Button>
        </Box>
      </Box>
    </AuthContainer>
  );
};
